import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUnit } from 'effector-react';
import { $isPayoutTraderActive, $payoutWorkingStatus, PayoutWorkingStatus } from '~/entities/payout';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    getTraderPayoutOrderServiceCountOrdersUrl,
    traderPayoutOrderServiceCountOrders,
} from '~/generated/payoutBackend/order';
import { TraderPayoutOrderServiceCountOrdersFilterStatusesItem } from '~/generated/payoutBackend/model';
import { setPayoutNewOrdersCount } from '../model';
import { REFETCH_INTERVAL } from './constants';
import { notifyOfNewOrders } from './helper';

const LS_TOTAL_KEY = 'payout_prev_orders_total_count';
const LS_WAITING_KEY = 'payout_prev_orders_waiting_count';

interface OrdersCountSnapshot {
    total: number;
    waiting: number;
}

const ordersTotalQuery = {};

const ordersWaitingQuery = {
    'filter.statuses': [TraderPayoutOrderServiceCountOrdersFilterStatusesItem.STATUS_WAITING_REACTION],
};

const readPersistedCount = (): OrdersCountSnapshot | null => {
    const rawTotal = localStorage.getItem(LS_TOTAL_KEY);
    const rawWaiting = localStorage.getItem(LS_WAITING_KEY);

    if (rawTotal === null || rawWaiting === null) {
        return null;
    }

    const total = Number(rawTotal);
    const waiting = Number(rawWaiting);

    if (!Number.isFinite(total) || !Number.isFinite(waiting)) {
        return null;
    }

    return {
        total,
        waiting,
    };
};

const persistCount = ({ total, waiting }: OrdersCountSnapshot) => {
    localStorage.setItem(LS_TOTAL_KEY, String(total));
    localStorage.setItem(LS_WAITING_KEY, String(waiting));
};

export const useWatchPayoutOrdersCount = () => {
    const { workingStatus, isTraderActive } = useUnit({
        workingStatus: $payoutWorkingStatus,
        isTraderActive: $isPayoutTraderActive,
    });
    const queryClient = useQueryClient();
    const prevCountRef = useRef<OrdersCountSnapshot | null>(readPersistedCount());

    const isWorkStarted = workingStatus === PayoutWorkingStatus.WORKING_STATUS_IN_WORK;

    const totalOrdersQuery = useQuery({
        enabled: isTraderActive,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        queryFn: async () => await traderPayoutOrderServiceCountOrders(ordersTotalQuery),
        queryKey: [queryKeys.PAYOUT_ORDERS_COUNT, getTraderPayoutOrderServiceCountOrdersUrl(ordersTotalQuery), 'total'],
    });

    const waitingOrdersQuery = useQuery({
        enabled: isTraderActive,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        queryFn: async () => await traderPayoutOrderServiceCountOrders(ordersWaitingQuery),
        queryKey: [
            queryKeys.PAYOUT_ORDERS_COUNT,
            getTraderPayoutOrderServiceCountOrdersUrl(ordersWaitingQuery),
            'waiting',
        ],
    });

    useEffect(() => {
        if (!isTraderActive) {
            prevCountRef.current = null;
            setPayoutNewOrdersCount(0);

            return;
        }

        if (!totalOrdersQuery.isSuccess || !waitingOrdersQuery.isSuccess) {
            return;
        }

        const total = Number(totalOrdersQuery.data?.pagination?.total) || 0;
        const waiting = Number(waitingOrdersQuery.data?.pagination?.total) || 0;
        const prevCount = prevCountRef.current;
        const currentCount = {
            total,
            waiting,
        };

        prevCountRef.current = currentCount;
        persistCount(currentCount);
        setPayoutNewOrdersCount(waiting);

        if (prevCount && total > prevCount.total && waiting !== prevCount.waiting) {
            void notifyOfNewOrders();

            if (isWorkStarted) {
                void queryClient.invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_ORDERS],
                });
            }
        }
    }, [
        isTraderActive,
        isWorkStarted,
        queryClient,
        totalOrdersQuery.data?.pagination?.total,
        totalOrdersQuery.isSuccess,
        waitingOrdersQuery.data?.pagination?.total,
        waitingOrdersQuery.isSuccess,
    ]);
};
